// frontend/src/dashboard_v2/DashboardV2.jsx

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useOutletContext, useLocation } from "react-router-dom";
import { buildProgressScenarios, progressScenarioOrder } from "../dev/progressScenarios";
import YouTubePlayer from "../components/YouTubePlayer";
import "./DashboardV2.css";

import { API_BASE_URL } from "../config/apiBase";
import { getVerificationOrigin } from "../config/verificationOrigin";
import { addDownloadCacheBuster } from "../utils/certificateDownloadUrl";
import { getAuthUserHeader, isAdminUser } from "../utils/userRoles";

const API = API_BASE_URL;

function formatLessonCompletedAt(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Karachi",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(parsed);
  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${get("day")} ${get("month")} ${get("year")} · ${get("hour").replace(/^0/, "")}:${get("minute")} ${get("dayPeriod").toUpperCase()} PKT`;
}

function normalizeScoreKey(key) {
  return String(key || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function safeNumber(value) {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeLessonRef(value) {
  if (!isPlainObject(value)) return null;
  const week = safeNumber(value.week);
  const lesson = safeNumber(value.lesson);
  if (week == null || lesson == null) return null;
  return { week, lesson };
}

function truthyText(value) {
  return String(value || "").trim().length > 0;
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeSignalLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const overrides = {
    SENSORY_DETAIL: "Sensory Detail",
    SPECIFIC_DETAIL: "Specific Detail",
    INTERNAL_THOUGHT: "Internal Thought",
    VERB_STRENGTH: "Verb Strength",
    EMOTION: "Emotion",
    ACTION: "Action",
    DIALOGUE: "Dialogue",
    REPETITION: "Repetition",
  };
  const upper = raw.toUpperCase();
  if (overrides[upper]) return overrides[upper];
  if (/[_-]/.test(raw)) {
    return raw
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase())
      .trim();
  }
  return raw;
}

function normalizeSignalEvidenceValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeSignalEvidenceValue(item))
      .filter(Boolean)
      .join(", ");
  }
  if (isPlainObject(value)) {
    return String(
      value.text ||
        value.quote ||
        value.evidence ||
        value.label ||
        value.signal ||
        value.title ||
        ""
    ).trim();
  }
  return String(value || "").trim();
}

function extractSignalEntriesFromSpans(rawLabel, spans) {
  const label = normalizeSignalLabel(rawLabel);
  const pieces = (Array.isArray(spans) ? spans : [])
    .map((span) => normalizeSignalEvidenceValue(span))
    .filter(Boolean);
  return pieces.length
    ? [{ label, evidence: pieces.join(", ") }]
    : label
    ? [{ label, evidence: "" }]
    : [];
}

function extractWritingSignalEntriesFromMap(sourceMap) {
  if (!isPlainObject(sourceMap)) return [];
  const entries = [];
  Object.entries(sourceMap).forEach(([rawKey, rawValue]) => {
    if (rawKey === "summary" || rawKey === "enrichment_alignment") return;
    if (Array.isArray(rawValue)) {
      entries.push(...extractSignalEntriesFromSpans(rawKey, rawValue));
      return;
    }
    if (isPlainObject(rawValue)) {
      const label = normalizeSignalLabel(rawValue.label || rawValue.signal || rawKey);
      const spanSource =
        rawValue.spans ||
        rawValue.quotes ||
        rawValue.items ||
        rawValue.highlights ||
        rawValue.evidence ||
        rawValue.examples ||
        [];
      const evidencePieces = Array.isArray(spanSource)
        ? spanSource.map((item) => normalizeSignalEvidenceValue(item)).filter(Boolean)
        : [normalizeSignalEvidenceValue(spanSource)].filter(Boolean);
      if (label || evidencePieces.length) {
        entries.push({
          label: label || normalizeSignalLabel(rawKey),
          evidence: evidencePieces.join(", "),
        });
      }
      return;
    }
    const label = normalizeSignalLabel(rawKey);
    const evidence = normalizeSignalEvidenceValue(rawValue);
    if (label || evidence) entries.push({ label, evidence });
  });
  return entries;
}

function extractWritingSignalEntriesFromArray(sourceArray, fallbackLabelPrefix = "Signal") {
  if (!Array.isArray(sourceArray)) return [];
  return sourceArray.flatMap((item, index) => {
    if (isPlainObject(item)) {
      const label = normalizeSignalLabel(item.label || item.signal || `${fallbackLabelPrefix} ${index + 1}`);
      const spanSource =
        item.spans ||
        item.quotes ||
        item.items ||
        item.highlights ||
        item.evidence ||
        item.examples ||
        item.text ||
        [];
      const evidencePieces = Array.isArray(spanSource)
        ? spanSource.map((value) => normalizeSignalEvidenceValue(value)).filter(Boolean)
        : [normalizeSignalEvidenceValue(spanSource)].filter(Boolean);
      if (!label && !evidencePieces.length) return [];
      return [
        {
          label: label || `${fallbackLabelPrefix} ${index + 1}`,
          evidence: evidencePieces.join(", "),
        },
      ];
    }
    const evidence = normalizeSignalEvidenceValue(item);
    if (!evidence) return [];
    return [{ label: `${fallbackLabelPrefix} ${index + 1}`, evidence }];
  });
}

function collectWritingSignalEntries(payload) {
  const sources = [
    payload?.writing_signals,
    payload?.feedback?.writing_signals,
    payload?.evaluation?.writing_signals,
    payload?.signals,
  ];
  const entries = [];

  sources.forEach((source) => {
    if (!source) return;
    if (Array.isArray(source)) {
      source.forEach((item, index) => {
        if (isPlainObject(item) && (item.signal || item.label)) {
          const label = normalizeSignalLabel(item.label || item.signal);
          const spanSource = item.spans || item.quotes || item.items || item.highlights || item.evidence || [];
          const evidencePieces = Array.isArray(spanSource)
            ? spanSource.map((value) => normalizeSignalEvidenceValue(value)).filter(Boolean)
            : [normalizeSignalEvidenceValue(spanSource)].filter(Boolean);
          if (label || evidencePieces.length) {
            entries.push({
              label: label || `Signal ${index + 1}`,
              evidence: evidencePieces.join(", "),
            });
          }
          return;
        }
        const evidence = normalizeSignalEvidenceValue(item);
        if (evidence) entries.push({ label: `Signal ${index + 1}`, evidence });
      });
      return;
    }
    if (isPlainObject(source)) {
      if (Array.isArray(source.writing_signals)) {
        entries.push(...extractWritingSignalEntriesFromArray(source.writing_signals));
      }
      entries.push(...extractWritingSignalEntriesFromMap(source.signals));
      entries.push(...extractWritingSignalEntriesFromArray(source.basic_annotations, "Annotation"));
      entries.push(...extractWritingSignalEntriesFromArray(source.enriched_annotations, "Annotation"));
      return;
    }
    const evidence = normalizeSignalEvidenceValue(source);
    if (evidence) entries.push({ label: "Signal", evidence });
  });

  if (Array.isArray(payload?.signal_feedback_lines)) {
    entries.push(...extractWritingSignalEntriesFromArray(payload.signal_feedback_lines));
  }

  return entries
    .filter((entry) => entry && (truthyText(entry.label) || truthyText(entry.evidence)))
    .filter((entry, index, all) => {
      const key = `${String(entry.label || "").trim().toLowerCase()}::${String(entry.evidence || "").trim().toLowerCase()}`;
      return all.findIndex((candidate) => {
        const candidateKey = `${String(candidate.label || "").trim().toLowerCase()}::${String(candidate.evidence || "").trim().toLowerCase()}`;
        return candidateKey === key;
      }) === index;
    });
}

function formatSignalEvidence(entry) {
  const label = String(entry?.label || "").trim();
  const evidence = String(entry?.evidence || "").trim();
  if (label && evidence) return `${label}: ${evidence}`;
  return label || evidence;
}

function normalizeLessonSections(lesson) {
  const sections = Array.isArray(lesson?.submission_sections) ? lesson.submission_sections : [];
  return sections
    .map((section) => ({
      key: String(section?.key || "").trim(),
      label: String(section?.label || section?.key || "").trim(),
      description: String(section?.description || "").trim(),
      placeholder: String(section?.placeholder || "").trim(),
      read_only: section?.read_only === true,
      content: String(section?.content || "").trim(),
    }))
    .filter((section) => section.key && section.label);
}

function parseStructuredSubmissionText(sections, draftText) {
  const text = String(draftText || "");
  const labels = (sections || [])
    .map((section) => ({
      key: String(section?.key || "").trim(),
      label: String(section?.label || section?.key || "").trim(),
    }))
    .filter((section) => section.key && section.label);
  if (!labels.length || !text.trim()) return {};

  const parsed = {};
  labels.forEach((section, index) => {
    const startToken = section.label;
    const startIndex = text.toLowerCase().indexOf(startToken.toLowerCase());
    if (startIndex < 0) {
      parsed[section.key] = "";
      return;
    }
    const bodyStart = startIndex + startToken.length;
    let bodyEnd = text.length;
    labels.slice(index + 1).forEach((nextSection) => {
      const nextIndex = text.toLowerCase().indexOf(nextSection.label.toLowerCase(), bodyStart);
      if (nextIndex >= 0 && nextIndex < bodyEnd) bodyEnd = nextIndex;
    });
    parsed[section.key] = text.slice(bodyStart, bodyEnd).replace(/^\s*[:\-–—]\s*/, "").trim();
  });
  return parsed;
}

function splitLooseSectionedText(draftText) {
  const lines = String(draftText || "")
    .replace(/\r/g, "")
    .split("\n");
  const sections = [];
  let current = null;

  const pushCurrent = () => {
    if (!current) return;
    const body = current.lines.join("\n").trim();
    if (current.label || body) {
      sections.push({
        key: current.key,
        label: current.label || current.key,
        description: "",
        value: body,
      });
    }
    current = null;
  };

  lines.forEach((rawLine, index) => {
    const line = String(rawLine || "").trim();
    if (!line) {
      if (current) current.lines.push("");
      return;
    }

    const headingMatch = line.match(
      /^(?:[-*•]\s*)?(?<label>(?:scene|part|section|step|reflection|revision note|character profile|dialogue task|pov\s*&\s*voice note|outline|weekly reflection|final reflection|short draft|feedback comments|feedback decision)(?:\s*\d+)?)\s*:?\s*(?<body>.*)$/i
    );
    const matches = headingMatch && headingMatch.groups?.label ? headingMatch : null;

    if (matches && matches.groups?.label) {
      pushCurrent();
      const label = String(matches.groups.label || "").trim();
      const body = String(matches.groups.body || "").trim();
      current = {
        key: normalizeScoreKey(label) || `section_${sections.length + 1}`,
        label,
        lines: body ? [body] : [],
      };
      return;
    }

    if (!current) {
      current = {
        key: `section_${sections.length + 1}`,
        label: index === 0 ? "Submission" : `Section ${sections.length + 1}`,
        lines: [line],
      };
      return;
    }

    current.lines.push(line);
  });

  pushCurrent();
  return sections.filter((section) => section.label || section.value);
}

function getLessonIdentity(lesson) {
  const week = toInt(lesson?.weekNum ?? lesson?.week, null);
  const lessonNum = toInt(lesson?.lesson, null);
  const lessonId =
    String(lesson?.id || lesson?.lesson_id || "").trim() || (week != null && lessonNum != null ? `W${week}L${lessonNum}` : "");
  return {
    lessonId,
    week,
    lessonNum,
  };
}

function toInt(v, fallback) {
  const n = parseInt(String(v ?? ""), 10);
  return Number.isNaN(n) ? fallback : n;
}

function lessonKey(week, lesson) {
  return `${toInt(week, 0)}:${toInt(lesson, 0)}`;
}

function lessonKeyFromLessonId(lessonId) {
  const raw = String(lessonId || "").trim().toUpperCase();
  if (/^\d+:\d+$/.test(raw)) return raw;
  const match = raw.match(/^W(\d+)L(\d+)$/);
  if (match) {
    return lessonKey(match[1], match[2]);
  }
  return "";
}

function isAbortError(err) {
  return (
    err?.name === "AbortError" ||
    err === "dashboard_v2_cleanup" ||
    String(err?.message || err || "").toLowerCase().includes("aborted")
  );
}

function normalizeTrendLesson(lesson) {
  const lessonId = lesson.lesson_id || `W${lesson.week}L${lesson.lesson}`;
  const attempts = Array.isArray(lesson.attempts) ? lesson.attempts : [];
  const final = lesson.final_best || lesson.final || attempts.reduce((best, attempt) => (
    !best || safeNumber(attempt.overall) > safeNumber(best.overall) ? attempt : best
  ), null);
  return {
    ...lesson,
    lesson_id: lessonId,
    topic: lesson.topic || lesson.title || lessonId,
    focus_metrics: lesson.focus_metrics || {},
    attempts,
    final,
    overall: final?.overall,
    creativity: final?.creativity,
    clarity: final?.clarity,
    task_completion: final?.task_completion,
  };
}

function buildCurriculumLessonMap(curriculum) {
  const map = new Map();
  const weeks = Array.isArray(curriculum?.weeks) ? curriculum.weeks : [];
  weeks.forEach((week) => {
    const weekNum = toInt(week?.week, null);
    (Array.isArray(week?.lessons) ? week.lessons : []).forEach((lesson) => {
      const lessonNum = toInt(lesson?.lesson, null);
      const lessonId = String(lesson?.id || (weekNum != null && lessonNum != null ? `W${weekNum}L${lessonNum}` : "")).trim();
      if (!lessonId) return;
      map.set(lessonId, {
        ...lesson,
        week: weekNum,
        lesson: lessonNum,
        id: lessonId,
      });
    });
  });
  return map;
}

function resolveProgressLessonVideoUrl(lesson, curriculumLesson) {
  const candidates = [
    lesson?.introVideoUrl,
    lesson?.intro_video,
    curriculumLesson?.introVideoUrl,
    curriculumLesson?.intro_video,
  ];
  for (const candidate of candidates) {
    const url = String(candidate || "").trim();
    if (url) return url;
  }
  return null;
}

function isYouTubeVideoUrl(url) {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace("www.", "");
    return host === "youtu.be" || host === "youtube.com" || host === "m.youtube.com";
  } catch {
    return false;
  }
}

function extractYouTubeVideoId(url) {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace("www.", "");
    if (host === "youtu.be") return parsed.pathname.slice(1).split("?")[0].split("&")[0];
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = parsed.searchParams.get("v");
      return (id || "").split("?")[0].split("&")[0];
    }
    return null;
  } catch {
    return null;
  }
}

function renderProgressLessonVideo(videoUrl) {
  if (!videoUrl) return null;

  if (isYouTubeVideoUrl(videoUrl)) {
    return (
      <div className="dv-progress-lesson-video-wrap">
        <YouTubePlayer
          videoId={extractYouTubeVideoId(videoUrl)}
          className="lp-video-compact dv-progress-lesson-video"
        />
      </div>
    );
  }

  return (
    <div className="dv-progress-lesson-video-wrap">
      <div className="lp-video lp-video-compact dv-progress-lesson-video">
        <video controls style={{ position: "absolute", inset: 0, width: "100%", height: "100%", borderRadius: 10 }}>
          <source src={videoUrl} />
        </video>
      </div>
    </div>
  );
}

function groupTrendPoints(points) {
  const grouped = new Map();
  points.forEach((point) => {
    const lessonId = point.lesson_id || `W${point.week}L${point.lesson}`;
    if (!grouped.has(lessonId)) {
      grouped.set(lessonId, {
        lesson_id: lessonId,
        week: point.week,
        lesson: point.lesson,
        topic: point.topic || lessonId,
        rubric_focus: point.rubric_focus,
        focus_metrics: point.focus_metrics || {},
        attempts: [],
      });
    }
    grouped.get(lessonId).attempts.push(point);
  });
  return Array.from(grouped.values()).map((lesson) => {
    lesson.attempts.sort((a, b) => toInt(a.attempt, 1) - toInt(b.attempt, 1));
    return normalizeTrendLesson(lesson);
  });
}

function RubricBars({ rubricJson }) {
  if (!rubricJson) return null;

  let raw = null;
  try {
    raw = typeof rubricJson === "string" ? JSON.parse(rubricJson) : rubricJson;
  } catch {
    return null;
  }

  if (!raw || typeof raw !== "object") return null;

  // Build a normalized lookup so keys like "task completion" work.
  const sourceScores =
    raw && typeof raw.scores_0_to_10 === "object" && !Array.isArray(raw.scores_0_to_10)
      ? raw.scores_0_to_10
      : raw;
  const normalized = {};
  for (const [k, v] of Object.entries(sourceScores)) {
    normalized[normalizeScoreKey(k)] = v;
  }

  const preferredOrder = ["task_completion", "clarity", "creativity"];
  const allKeys = Object.keys(normalized);
  const orderedKeys = [
    ...preferredOrder.filter((k) => allKeys.includes(k)),
    ...allKeys.filter((k) => !preferredOrder.includes(k)),
  ];

  const labelMap = {
    task_completion: "Task Completion",
    clarity: "Clarity",
    creativity: "Creativity",
  };

  const rows = orderedKeys
    .map((key) => {
      const score = safeNumber(normalized[key]);
      if (score == null) return null;
      const max = 10;
      const clamped = Math.max(0, Math.min(max, score));
      const pct = (clamped / max) * 100;
      const label = labelMap[key] || key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
      return { key, label, score: clamped, max, pct };
    })
    .filter(Boolean);

  if (rows.length === 0) return null;

  const totalScore = rows.reduce((acc, r) => acc + r.score, 0);
  const totalMax = rows.reduce((acc, r) => acc + r.max, 0);

  return (
    <div className="dv-rubric">
      <div className="dv-rubric-head">
        <div className="dv-rubric-title">Scores</div>
        <div className="dv-rubric-total">
          Total: <b>{totalScore}</b>/<b>{totalMax}</b>
        </div>
      </div>
      <div className="dv-rubric-rows">
        {rows.map((r) => (
          <div key={r.key} className="dv-rubric-row">
            <div className="dv-rubric-label">{r.label}</div>
            <div className="dv-rubric-bar" aria-hidden>
              <div className="dv-rubric-fill" style={{ width: `${r.pct}%` }} />
            </div>
            <div className="dv-rubric-score">
              <b>{r.score}</b>/<span>{r.max}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendGraph({ trend }) {
  const rawPoints = Array.isArray(trend?.points) ? trend.points : [];
  const rawLessons = Array.isArray(trend?.lessons) ? trend.lessons : null;
  const lessonItems = rawLessons
    ? rawLessons.map(normalizeTrendLesson)
    : groupTrendPoints(rawPoints);
  const [visibleMetrics, setVisibleMetrics] = useState({
    overall: true,
    creativity: false,
    clarity: false,
    task_completion: false,
  });

  const metricOptions = [
    { key: "overall", label: "Overall" },
    { key: "creativity", label: "Creativity" },
    { key: "clarity", label: "Clarity" },
    { key: "task_completion", label: "Task Completion" },
  ];

  const toggleMetric = (key) => {
    setVisibleMetrics((current) => {
      const visibleCount = Object.values(current).filter(Boolean).length;
      if (current[key] && visibleCount <= 1) return current;
      return { ...current, [key]: !current[key] };
    });
  };

  if (lessonItems.length === 0) {
    return (
      <section className="dv-card dv-trend reveal" style={{ "--delay": ".08s" }}>
        <h2 className="dv-h2">Improvement Trend</h2>
        <p className="dv-sub">No submissions yet. Submit your first lesson to see your improvement/decline graph.</p>
      </section>
    );
  }

  const width = 760;
  const height = 260;
  const pad = { top: 28, right: 12, bottom: 42, left: 42 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const valueToY = (value) => pad.top + ((100 - value) / 100) * plotH;
  const indexToX = (index) => {
    if (lessonItems.length <= 1) return pad.left + plotW / 2;
    return pad.left + (index / (lessonItems.length - 1)) * plotW;
  };
  const lessonStep = lessonItems.length > 1 ? plotW / (lessonItems.length - 1) : plotW;

  const palette = {
    overall: "#c9a56a",
    creativity: "#63b3ff",
    clarity: "#5ad39a",
    task_completion: "#f1995b",
  };

  const series = [
    { key: "overall", label: "Overall", color: palette.overall },
    { key: "creativity", label: "Creativity", color: palette.creativity },
    { key: "clarity", label: "Clarity", color: palette.clarity },
    { key: "task_completion", label: "Task Completion", color: palette.task_completion },
  ]
    .filter((s) => visibleMetrics[s.key])
    .map((s) => {
      const values = lessonItems
        .map((lesson, i) => ({
          x: indexToX(i),
          y: lesson[s.key] == null ? null : valueToY(lesson[s.key]),
          value: lesson[s.key],
          lesson,
        }))
        .filter((p) => p.y != null);
      return { ...s, values };
    })
    .filter((s) => s.values.length > 0);

  const firstAttempt = lessonItems[0]?.attempts?.[0];
  const finalPoint = lessonItems[lessonItems.length - 1]?.final || lessonItems[lessonItems.length - 1];
  const overallStart = firstAttempt?.overall ?? lessonItems[0]?.overall;
  const overallEnd = finalPoint?.overall;
  const overallDelta =
    typeof overallStart === "number" && typeof overallEnd === "number"
      ? Number((overallEnd - overallStart).toFixed(1))
      : null;
  const milestoneLabels = new Set(["W1L1", "W1L4", "W2L4", "W3L4", "W4L4"]);
  const scoreCategory = (score) => {
    if (score >= 90) return { label: "Excellent", className: "is-excellent" };
    if (score >= 80) return { label: "Good", className: "is-good" };
    return { label: "Pass", className: "is-pass" };
  };
  const weekGroups = [];
  lessonItems.forEach((lesson, index) => {
    const current = weekGroups.find((group) => group.week === lesson.week);
    if (current) current.end = index;
    else weekGroups.push({ week: lesson.week, start: index, end: index });
  });

  const markerTitle = (lesson, attempt, metricLabel, score) => {
    const focus = lesson.focus_metrics || {};
    return [
      `Lesson ID: ${lesson.lesson_id}`,
      `Lesson: ${lesson.topic || lesson.lesson_id}`,
      lesson.rubric_focus ? `Rubric Focus: ${lesson.rubric_focus}` : null,
      `Primary Focus: ${focus.primary || "Balanced"}`,
      focus.secondary ? `Secondary Focus: ${focus.secondary}` : null,
      focus.tertiary ? `Tertiary Focus: ${focus.tertiary}` : null,
      `Score: ${score}`,
      `Attempt: ${attempt.attempt}`,
      `${attempt.label || `${lesson.lesson_id} · A${attempt.attempt}`} · ${metricLabel}`,
    ].filter(Boolean).join("\n");
  };

  return (
    <section className="dv-card dv-trend reveal" style={{ "--delay": ".08s" }}>
      <div className="dv-trend-head">
        <h2 className="dv-h2">Improvement Trend</h2>
        <div className="dv-trend-meta">
          Latest overall: <b>{typeof overallEnd === "number" ? overallEnd : "—"}</b>/100
          {overallDelta != null && (
            <span className={overallDelta >= 0 ? "dv-delta up" : "dv-delta down"}>
              {overallDelta >= 0 ? `+${overallDelta}` : `${overallDelta}`} from first attempt
            </span>
          )}
        </div>
      </div>

      <div className="dv-metric-toggle-row" aria-label="Trend metric controls">
        <span className="dv-metric-toggle-label">Show metrics:</span>
        {metricOptions.map((metric) => (
          <button
            key={metric.key}
            type="button"
            className={`dv-metric-chip ${visibleMetrics[metric.key] ? "is-active" : ""}`.trim()}
            onClick={() => toggleMetric(metric.key)}
            aria-pressed={visibleMetrics[metric.key] ? "true" : "false"}
          >
            <i style={{ background: palette[metric.key] }} />
            {metric.label}
          </button>
        ))}
      </div>
      {lessonItems.some((lesson) => Array.isArray(lesson.attempts) && lesson.attempts.length > 0) ? (
        <div className="dv-attempt-legend" aria-label="Attempt marker legend">
          <span><i className="is-hollow" /> Attempt 1</span>
          <span><i className="is-filled" /> Attempt 2</span>
        </div>
      ) : null}

      <div className="dv-graph-wrap">
        <svg viewBox={`0 0 ${width} ${height}`} className="dv-graph" role="img" aria-label="Student improvement trend graph">
          {[0, 25, 50, 75, 100].map((tick) => {
            const y = valueToY(tick);
            return (
              <g key={tick}>
                <line x1={pad.left} y1={y} x2={width - pad.right} y2={y} className="dv-grid-line" />
                <text x={8} y={y + 4} className="dv-axis-text">{tick}</text>
              </g>
            );
          })}

          {lessonItems.map((lesson, idx) => {
            const x = indexToX(idx);
            return (
              <line
                key={`lesson-grid-${lesson.lesson_id}`}
                x1={x}
                y1={pad.top}
                x2={x}
                y2={height - pad.bottom}
                className="dv-lesson-grid-line"
              />
            );
          })}

          {weekGroups.slice(0, -1).map((group) => {
            const x = Math.min(width - pad.right, indexToX(group.end) + lessonStep / 2);
            return (
              <line
                key={`week-separator-${group.week}`}
                x1={x}
                y1={pad.top - 16}
                x2={x}
                y2={height - pad.bottom}
                className="dv-week-separator-line"
              />
            );
          })}

          {weekGroups.map((group) => {
            const x = (indexToX(group.start) + indexToX(group.end)) / 2;
            return (
              <text key={`week-label-${group.week}`} x={x} y={12} textAnchor="middle" className="dv-week-axis-text">
                Week {group.week}
              </text>
            );
          })}

          {series.map((s) => {
            const polyline = s.values.map((p) => `${p.x},${p.y}`).join(" ");
            return <polyline key={s.key} points={polyline} fill="none" stroke={s.color} strokeWidth="2.5" />;
          })}

          {series.map((s) => (
            <g key={`${s.key}-markers`}>
              {s.values.flatMap((v) => {
                const attempts = Array.isArray(v.lesson.attempts) ? v.lesson.attempts : [];
                return attempts
                  .filter((attempt) => typeof attempt[s.key] === "number")
                  .map((attempt) => {
                    const isAttemptTwo = Number(attempt.attempt) === 2;
                    const score = attempt[s.key];
                    return (
                      <circle
                        key={`${s.key}-${v.lesson.lesson_id}-${attempt.attempt}`}
                        cx={v.x}
                        cy={valueToY(score)}
                        r={isAttemptTwo ? 3.4 : 3}
                        fill={isAttemptTwo ? s.color : "#121314"}
                        stroke={s.color}
                        strokeWidth="1.8"
                      >
                        <title>{markerTitle(v.lesson, attempt, s.label, score)}</title>
                      </circle>
                    );
                  });
              })}
            </g>
          ))}

          {lessonItems.map((lesson, idx) => {
            const show = milestoneLabels.has(lesson.lesson_id);
            if (!show) return null;
            return (
              <text key={`axis-${lesson.lesson_id}`} x={indexToX(idx)} y={height - 16} textAnchor="middle" className="dv-axis-text">
                {lesson.lesson_id}
              </text>
            );
          })}
        </svg>
      </div>
      {lessonItems.length ? (
        <div className="dv-best-score-panel">
          <div className="dv-best-score-title">Final Best Score per Lesson</div>
          <div className="dv-best-score-grid">
            {lessonItems.map((lesson) => {
              const score = lesson.overall;
              const category = scoreCategory(score);
              return (
                <div key={lesson.lesson_id} className="dv-best-score-item">
                  <span className="dv-best-score-lesson">{lesson.lesson_id}</span>
                  <span className="dv-best-score-value">{score}</span>
                  <span className={`dv-score-badge ${category.className}`}>{category.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function SummaryPanel({ summary }) {
  if (!summary) return null;

  const arrows = summary.trend_arrows || {};
  const arrowToWord = (a) => (a === "up" ? "improving" : a === "down" ? "slipping" : "steady");
  const supportLabel = (level) => {
    if (level === "low") return "Light support";
    if (level === "med") return "Guided support";
    if (level === "high") return "Step-by-step support";
    if (level === "intervention") return "Extra support";
    return "Support adapting to your progress";
  };
  const momentumLabel = (status) => {
    if (status === "improving") return "You are improving.";
    if (status === "plateauing") return "You are steady right now.";
    if (status === "declining") return "You have dipped recently.";
    return "Keep going. We need a little more work to judge momentum.";
  };
  const latestScores = Array.isArray(summary.latest_score_per_lesson) ? summary.latest_score_per_lesson : [];

  return (
    <section className="dv-card dv-summary reveal" style={{ "--delay": ".07s" }}>
      <h2 className="dv-h2">Your Writing Snapshot</h2>
      <div className="dv-summary-grid">
        <div className="dv-summary-item">
          <div className="dv-summary-k">Your latest scores</div>
          {latestScores.length === 0 ? (
            <div className="dv-summary-v">No scores yet</div>
          ) : (
            <div className="dv-score-list">
              {latestScores.map((row) => (
                <span key={`${row.week}-${row.lesson}`} className="dv-chip">
                  {row.label}: {row.rubric_score == null ? "—" : `${row.rubric_score}/100`}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="dv-summary-item">
          <div className="dv-summary-k">Main focus right now</div>
          <div className="dv-summary-v">{summary.dominant_weak_area || "Building core writing skills"}</div>
        </div>

        <div className="dv-summary-item">
          <div className="dv-summary-k">How your skills are moving</div>
          <div className="dv-arrow-row">
            <span>Overall: {arrowToWord(arrows.overall)}</span>
            <span>Creativity: {arrowToWord(arrows.creativity)}</span>
            <span>Clarity: {arrowToWord(arrows.clarity)}</span>
            <span>Task: {arrowToWord(arrows.task_completion)}</span>
          </div>
        </div>

        <div className="dv-summary-item">
          <div className="dv-summary-k">Support level</div>
          <div className="dv-summary-v">{supportLabel(summary.current_scaffolding_level)}</div>
        </div>

        <div className="dv-summary-item">
          <div className="dv-summary-k">Your momentum</div>
          <div className="dv-summary-v">{momentumLabel(summary.trajectory_status)}</div>
        </div>
      </div>
    </section>
  );
}

export default function DashboardV2({
  outletContext,
  dashboardEndpoint,
  dashboardHeaders,
  disableStandaloneNav = false,
  initialSelectedLessonKey = "",
} = {}) {
  const params = useParams();
  const studentId = params.studentId || params.id;
  const navigate = useNavigate();
  const location = useLocation();
  const routeOutlet = useOutletContext() || {};
  const outlet = outletContext || routeOutlet;
  const hasPortalShell = Boolean(outlet && Object.keys(outlet).length > 0);
  const inPortal = Boolean(params.id) && !params.studentId;
  const authStudentId = String(localStorage.getItem("student_id") || studentId || "").toUpperCase();
  const authRole = localStorage.getItem("role") || "";
  const isAdmin = isAdminUser(authStudentId, authRole);
  const closeSidebarPanel = inPortal ? outlet.closeSidebarPanel : null;
  const [data, setData] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [lessonState, setLessonState] = useState(null);
  const [selectedLessonKey, setSelectedLessonKey] = useState(initialSelectedLessonKey);
  const [expandedSignalsLessonKey, setExpandedSignalsLessonKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [devProgressEnabled, setDevProgressEnabled] = useState(false);
  const [progressTestActive, setProgressTestActive] = useState(false);
  const [progressTestRunning, setProgressTestRunning] = useState(false);
  const [progressTestScenarioName, setProgressTestScenarioName] = useState("");
  const [progressScenarios, setProgressScenarios] = useState(null);
  const [dashboardRefreshTick, setDashboardRefreshTick] = useState(0);
  const [certificateState, setCertificateState] = useState(null);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [certificateError, setCertificateError] = useState("");
  const progressTestTimerRef = useRef(null);
  const autoScrollLessonRef = useRef(new Map());
  const autoScrollCompletedRef = useRef(false);
  const showProgressTestTools = Boolean(devProgressEnabled && isAdmin);
  const showCertificateGenerator = isAdmin;
  const shellCurrentWeek = toInt(outlet?.currentWeek, null);
  const shellCurrentLesson = toInt(outlet?.currentLesson, null);
  const hasShellCursor = hasPortalShell && Boolean(outlet?.dashboardReady) && shellCurrentWeek != null && shellCurrentLesson != null;
  const curriculumLessonMap = useMemo(() => buildCurriculumLessonMap(curriculum), [curriculum]);
  const resolvedDashboardEndpoint = dashboardEndpoint || `${API}/student/${studentId}/dashboard_v2`;
  const lessonFocusKey = useMemo(() => {
    const searchParams = new URLSearchParams(location.search || "");
    const fromSearch = String(searchParams.get("lesson_id") || searchParams.get("lessonId") || "").trim();
    const fromState = String(location.state?.lesson_id || location.state?.lessonId || location.state?.openLessonId || "").trim();
    return lessonKeyFromLessonId(fromSearch || fromState || "");
  }, [location.search, location.state]);
  const autoScrollLessonKey = useMemo(
    () => lessonKeyFromLessonId(String(location.state?.openLessonId || "").trim()),
    [location.state]
  );
  const shouldAutoScrollLesson = Boolean(location.state?.scrollToLesson && autoScrollLessonKey);

  useEffect(() => {
    setSelectedLessonKey(initialSelectedLessonKey || "");
  }, [initialSelectedLessonKey]);

  useEffect(() => {
    if (!lessonFocusKey) return;
    setSelectedLessonKey(lessonFocusKey);
  }, [lessonFocusKey]);

  useEffect(() => {
    autoScrollCompletedRef.current = false;
  }, [autoScrollLessonKey]);

  useEffect(() => {
    let cancelled = false;
    async function loadConfig() {
      try {
        const res = await fetch(`${API}/api/config`, {
          cache: "no-store",
          headers: {
            "X-User": getAuthUserHeader(),
          },
        });
        const json = await res.json().catch(() => null);
        if (!cancelled) {
          const devTools = json?.dev_tools || json?.DEV_TOOLS || {};
          setDevProgressEnabled(Boolean(devTools?.DEV_PROGRESS || json?.dev_progress_enabled));
        }
      } catch {
        if (!cancelled) setDevProgressEnabled(false);
      }
    }
    loadConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  const clearProgressTestTimer = useCallback(() => {
    if (progressTestTimerRef.current) {
      window.clearInterval(progressTestTimerRef.current);
      progressTestTimerRef.current = null;
    }
  }, []);

  const refreshDashboardV2 = useCallback(() => {
    clearProgressTestTimer();
    setLoading(true);
    setDashboardRefreshTick((current) => current + 1);
  }, [clearProgressTestTimer]);

  function applyProgressScenario(name, scenarioSource = progressScenarios) {
    const scenario = scenarioSource?.[name];
    if (!scenario) return;
    setData(scenario.dashboardJson);
    setCursor({
      week: toInt(scenario.progressJson?.week, 1),
      lesson: toInt(scenario.progressJson?.lesson, 1),
    });
    setSelectedLessonKey("");
    setLoading(false);
    setProgressTestScenarioName(name);
  }

  async function loadDevCurriculum() {
    const res = await fetch(`${API}/curriculum`, { cache: "no-store" });
    if (!res.ok) throw new Error("Unable to load curriculum for progress test.");
    return res.json();
  }

  async function startProgressTest() {
    if (!showProgressTestTools) return;
    clearProgressTestTimer();
    const curriculum = await loadDevCurriculum();
    const scenarios = buildProgressScenarios(curriculum);
    setProgressScenarios(scenarios);
    setProgressTestActive(true);
    setProgressTestRunning(true);
    let index = 0;
    applyProgressScenario(progressScenarioOrder[index], scenarios);
    progressTestTimerRef.current = window.setInterval(() => {
      const nextIndex = index + 1;
      if (nextIndex >= progressScenarioOrder.length) {
        clearProgressTestTimer();
        setProgressTestRunning(false);
        return;
      }
      index = nextIndex;
      applyProgressScenario(progressScenarioOrder[index], scenarios);
      if (index === progressScenarioOrder.length - 1) {
        clearProgressTestTimer();
        setProgressTestRunning(false);
      }
    }, 2500);
  }

  function stopProgressTest() {
    clearProgressTestTimer();
    setProgressTestRunning(false);
  }

  const resetProgressTest = useCallback(() => {
    clearProgressTestTimer();
    setProgressTestActive(false);
    setProgressTestRunning(false);
    setProgressTestScenarioName("");
    setData(null);
    setCursor(null);
    setSelectedLessonKey("");
    setLoading(true);
    setDashboardRefreshTick((current) => current + 1);
  }, [clearProgressTestTimer]);

  const generateTestCertificate = useCallback(async () => {
    if (!showCertificateGenerator || certificateLoading) return;
    setCertificateLoading(true);
    setCertificateError("");
    const downloadToken = `${Date.now()}`;
    try {
      const certStudentId = String(data?.student?.student_id || studentId || authStudentId || "").trim().toUpperCase();
      const res = await fetch(`${API}/api/admin/students/${encodeURIComponent(certStudentId)}/certificate/generate-test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User": getAuthUserHeader(),
        },
        body: JSON.stringify({
          verification_origin: getVerificationOrigin(),
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || json?.ok === false) {
        throw new Error((json && (json.detail || json.error)) || "Failed to generate test certificate.");
      }
      setCertificateState({
        ...json,
        download_url: addDownloadCacheBuster(json.download_url, downloadToken),
      });
    } catch (err) {
      setCertificateError(err instanceof Error ? err.message : "Failed to generate test certificate.");
    } finally {
      setCertificateLoading(false);
    }
  }, [authStudentId, certificateLoading, data?.student?.student_id, showCertificateGenerator, studentId]);

  useEffect(() => {
    if (!showProgressTestTools && progressTestActive) {
      resetProgressTest();
    }
  }, [showProgressTestTools, progressTestActive, resetProgressTest]);

  useEffect(() => {
    if (progressTestActive) return undefined;
    if (hasPortalShell && !outlet?.dashboardReady) return undefined;
    // No interval polling here: reload only on mount, explicit progress events, or manual refresh.
    let cancelled = false;
    async function loadDashboard() {
      try {
        const refreshSuffix = dashboardRefreshTick ? `&refresh=${dashboardRefreshTick}` : "";
        const dashboardUrl = dashboardRefreshTick
          ? `${resolvedDashboardEndpoint}${resolvedDashboardEndpoint.includes("?") ? "&" : "?"}refresh=${dashboardRefreshTick}`
          : resolvedDashboardEndpoint;
        const curriculumPromise = fetch(`${API}/curriculum${refreshSuffix}`, { cache: "no-store" });
        const lessonStatePromise = fetch(`${API}/api/lesson-state/${encodeURIComponent(studentId)}${refreshSuffix}`, {
          cache: "no-store",
        });
        const dashboardPromise = fetch(dashboardUrl, {
          cache: "no-store",
          headers: dashboardHeaders,
        });
        const progressPromise = hasShellCursor
          ? null
          : fetch(`${API}/api/dashboard?student_id=${encodeURIComponent(studentId)}${refreshSuffix}`, {
              cache: "no-store",
            });

        const [curriculumRes, lessonStateRes, dashboardRes, progressRes] = await Promise.all(
          hasShellCursor
            ? [curriculumPromise, lessonStatePromise, dashboardPromise]
            : [curriculumPromise, lessonStatePromise, dashboardPromise, progressPromise]
        );

        const [curriculumJson, lessonStateJson, dashboardJson, progressJson] = await Promise.all([
          curriculumRes.json().catch(() => null),
          lessonStateRes.json().catch(() => null),
          dashboardRes.json(),
          hasShellCursor ? Promise.resolve(null) : progressRes.json().catch(() => null),
        ]);

        if (cancelled) return;
        setCurriculum(curriculumJson || null);
        if (!dashboardRes.ok) {
          setData({ error: dashboardJson?.detail || "Unable to load dashboard." });
        } else {
          setData(dashboardJson);
          setLessonState(lessonStateJson);
          setCursor({
            week: hasShellCursor ? shellCurrentWeek : toInt(progressJson?.week, 1),
            lesson: hasShellCursor ? shellCurrentLesson : toInt(progressJson?.lesson, 1),
          });
        }
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("DashboardV2 error:", err);
        setData({ error: "Network or server error." });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [
    studentId,
    progressTestActive,
    dashboardRefreshTick,
    hasShellCursor,
    hasPortalShell,
    outlet?.dashboardReady,
    shellCurrentWeek,
    shellCurrentLesson,
    resolvedDashboardEndpoint,
    dashboardHeaders,
  ]);

  useEffect(() => {
    const handleProgressRefetch = (event) => {
      const sid = String(event?.detail?.studentId || "").toUpperCase();
      if (sid && sid !== String(studentId || "").toUpperCase()) return;
      const nextWeek = toInt(event?.detail?.currentWeek, null);
      const nextLesson = toInt(event?.detail?.currentLesson, null);
      if (nextWeek != null && nextLesson != null) {
        setCursor({ week: nextWeek, lesson: nextLesson });
      }
      refreshDashboardV2();
    };
    window.addEventListener("studentProgressRefetch", handleProgressRefetch);
    return () => {
      window.removeEventListener("studentProgressRefetch", handleProgressRefetch);
    };
  }, [studentId, refreshDashboardV2]);

  useEffect(() => {
    return () => clearProgressTestTimer();
  }, [clearProgressTestTimer]);

  const currentWeek = toInt(cursor?.week, 1);
  const currentLesson = toInt(cursor?.lesson, 1);
  const currentLessonClosed = useMemo(() => {
    const lessons = Array.isArray(lessonState?.lessons) ? lessonState.lessons : [];
    return lessons.some(
      (r) =>
        toInt(r?.week, null) === currentWeek &&
        toInt(r?.lesson, null) === currentLesson &&
        Boolean(r?.closed)
    );
  }, [lessonState, currentWeek, currentLesson]);
  const nextLesson = useMemo(() => normalizeLessonRef(data?.next_lesson), [data?.next_lesson]);
  const nextLessonLocked =
    typeof data?.next_lesson_locked === "boolean"
      ? data.next_lesson_locked
      : Boolean(nextLesson && !currentLessonClosed);

  const weekCards = useMemo(() => {
    const weeks = Array.isArray(data?.weeks) ? data.weeks : [];
    return weeks.map((week) => {
      const weekNum = toInt(week?.week, 1);
      const isPastWeek = weekNum < currentWeek;
      const isCurrentWeek = weekNum === currentWeek;
      const hasBackendUnlockedNextLesson =
        Boolean(nextLesson && !nextLessonLocked && nextLesson.week === weekNum);
      const isFutureWeek = weekNum > currentWeek && !hasBackendUnlockedNextLesson;
      const isUnlockedFutureWeek = weekNum > currentWeek && hasBackendUnlockedNextLesson;

      const lessons = Array.isArray(week?.lessons) ? week.lessons : [];
      const mappedLessons = lessons.map((lesson) => {
        const lessonNum = toInt(lesson?.lesson, 1);
        const isClosedCurrent = isCurrentWeek && lessonNum === currentLesson && currentLessonClosed;
        const isCurrent = isCurrentWeek && lessonNum === currentLesson && !currentLessonClosed;
        const isPastInCurrentWeek = isCurrentWeek && lessonNum < currentLesson;
        const isNext =
          isCurrentWeek && lessonNum === currentLesson + 1 && currentLessonClosed;
        const isBackendUnlockedNext =
          Boolean(
            nextLesson &&
              !nextLessonLocked &&
              weekNum === nextLesson.week &&
              lessonNum === nextLesson.lesson
          );
        const isLaterInCurrentWeek = isCurrentWeek && lessonNum > currentLesson + 1;
        const isCompleted = isPastWeek || isPastInCurrentWeek || isClosedCurrent;

        const status = isCompleted
          ? "Completed"
          : isCurrent
          ? "In Progress"
          : isNext || isBackendUnlockedNext
          ? "Ready"
          : "Locked";

        const showTask =
          isPastWeek ||
          isCurrent ||
          isPastInCurrentWeek ||
          isNext ||
          isBackendUnlockedNext ||
          isClosedCurrent;
        const lockedReason = isFutureWeek
          ? ""
          : isLaterInCurrentWeek
          ? "Complete previous lesson to unlock."
          : isCurrentWeek && lessonNum === currentLesson + 1 && !currentLessonClosed
          ? "Complete the current lesson to unlock the next one."
          : isNext || isBackendUnlockedNext
          ? "Ready to start when current lesson is complete."
          : "";

        return {
          ...lesson,
          weekNum,
          lessonNum,
          status,
          showTask,
          isLocked: status === "Locked",
          lockedReason,
        };
      });

      return {
        weekNum,
        isPastWeek,
        isCurrentWeek,
        isFutureWeek,
        isUnlockedFutureWeek,
        lessons: mappedLessons,
      };
    });
  }, [data?.weeks, currentWeek, currentLesson, currentLessonClosed, nextLesson, nextLessonLocked]);

  const selectedLesson = useMemo(() => {
    if (!selectedLessonKey) return null;
    const lessons = [];
    for (const week of weekCards) {
      for (const lesson of week.lessons || []) {
        lessons.push({ ...lesson, weekNum: week.weekNum });
      }
    }
    if (!lessons.length) return null;
    return lessons.find((lesson) => lessonKey(lesson.weekNum, lesson.lesson) === selectedLessonKey) || null;
  }, [weekCards, selectedLessonKey]);
  const selectedLessonWeekNum = selectedLesson?.weekNum ?? null;

  useEffect(() => {
    if (!selectedLessonKey) return;
    if (loading || !data) return;
    if (selectedLesson) return;
    setSelectedLessonKey("");
  }, [selectedLessonKey, selectedLesson, data, loading]);

  useEffect(() => {
    if (!shouldAutoScrollLesson) return;
    if (autoScrollCompletedRef.current) return;
    if (loading || !data || !selectedLesson) return;
    if (selectedLessonKey !== autoScrollLessonKey) return;

    const node = autoScrollLessonRef.current.get(selectedLessonKey);
    if (!node || typeof node.getBoundingClientRect !== "function" || typeof node.scrollIntoView !== "function") return;

    const rect = node.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const alreadyVisible = rect.top >= 0 && rect.bottom <= viewportHeight;
    if (alreadyVisible) {
      autoScrollCompletedRef.current = true;
      return;
    }

    autoScrollCompletedRef.current = true;
    window.requestAnimationFrame(() => {
      node.scrollIntoView({ block: "start", behavior: "smooth" });
    });
  }, [autoScrollLessonKey, data, loading, selectedLesson, selectedLessonKey, shouldAutoScrollLesson]);

  const renderLessonRichContent = (lesson) => {
    const { lessonId } = getLessonIdentity(lesson);
    const curriculumLesson = lessonId ? curriculumLessonMap.get(lessonId) || null : null;
    const lessonVideoUrl = resolveProgressLessonVideoUrl(lesson, curriculumLesson);
    const resultJson = lesson?.result_json && typeof lesson.result_json === "object" ? lesson.result_json : {};
    const feedbackPayload =
      resultJson.feedback && typeof resultJson.feedback === "object" ? resultJson.feedback : {};
    const studentFeedback =
      resultJson.student_feedback && typeof resultJson.student_feedback === "object"
        ? resultJson.student_feedback
        : {};
    const writingSignals =
      resultJson.writing_signals && typeof resultJson.writing_signals === "object"
        ? resultJson.writing_signals
        : {};
    const signalSummary =
      writingSignals.summary && typeof writingSignals.summary === "object"
        ? writingSignals.summary
        : {};
    const signalLines = Array.isArray(resultJson.signal_feedback_lines)
      ? resultJson.signal_feedback_lines.filter((line) => String(line || "").trim())
      : [];
    const attempts = Array.isArray(lesson?.attempts) ? lesson.attempts : [];
    const bestAttempt = lesson?.final_best && typeof lesson.final_best === "object" ? lesson.final_best : null;
    const score =
      toInt(resultJson.score, null) ??
      toInt(lesson?.score, null) ??
      toInt(lesson?.rubric_score, null);
    const attempt =
      toInt(lesson?.attempt, null) ??
      toInt(resultJson.attempt, null);
    const passed =
      typeof resultJson.pass_recommendation === "boolean"
        ? resultJson.pass_recommendation
        : null;
    const modernFeedbackPresent =
      truthyText(feedbackPayload.creativity) ||
      truthyText(feedbackPayload.clarity) ||
      truthyText(feedbackPayload.task_completion) ||
      truthyText(studentFeedback.coach_feedback_paragraph);
    const feedbackSections = [
      { label: "Creativity", text: String(feedbackPayload.creativity || "").trim() },
      { label: "Clarity", text: String(feedbackPayload.clarity || "").trim() },
      { label: "Task Completion", text: String(feedbackPayload.task_completion || "").trim() },
    ].filter((section) => truthyText(section.text));
    const coachFeedbackText = String(studentFeedback.coach_feedback_paragraph || "").trim();
    const legacyCoachFeedback = String(
      lesson?.feedback?.comments ||
        studentFeedback.feedback_text ||
        studentFeedback.coach_feedback ||
        resultJson.feedback_text ||
        ""
    ).trim();
    const signalSummaryParts = [];
    if (String(signalSummary.source || "").trim()) signalSummaryParts.push(String(signalSummary.source).trim());
    const basicCount = toInt(signalSummary.basic_annotations_count, null);
    const enrichedCount = toInt(signalSummary.enriched_annotations_count, null);
    if (basicCount != null || enrichedCount != null) {
      signalSummaryParts.push(
        `${basicCount != null ? basicCount : 0} basic${enrichedCount != null ? `, ${enrichedCount} enriched` : ""}`
      );
    }
    const signalSummaryLine = signalSummaryParts.join(" · ");
    const writingSignalEntries = collectWritingSignalEntries(resultJson);
    const hasWritingSignals = writingSignalEntries.length > 0;
    const signalAccordionOpen = expandedSignalsLessonKey === lessonId;
    const submissionSections = normalizeLessonSections(curriculumLesson || lesson);
    const structuredSubmission =
      lesson?.structured_submission && typeof lesson.structured_submission === "object" && !Array.isArray(lesson.structured_submission)
        ? lesson.structured_submission
        : resultJson?.structured_submission && typeof resultJson.structured_submission === "object" && !Array.isArray(resultJson.structured_submission)
        ? resultJson.structured_submission
        : null;
    const parsedStructuredSubmission =
      submissionSections.length > 1
        ? structuredSubmission && Object.keys(structuredSubmission).length > 0
          ? structuredSubmission
          : parseStructuredSubmissionText(submissionSections, lesson?.draft_text || "")
        : null;
    const structuredSectionView =
      parsedStructuredSubmission
        ? submissionSections.map((section) => ({
            ...section,
            value: String(
              section.read_only
                ? section.content || parsedStructuredSubmission?.[section.key] || ""
                : parsedStructuredSubmission?.[section.key] || ""
            ).trim(),
          }))
        : splitLooseSectionedText(lesson?.draft_text || "").length > 1
        ? splitLooseSectionedText(lesson?.draft_text || "")
        : null;
    const hasStoredReview =
      truthyText(lesson.draft_text) ||
      score != null ||
      truthyText(lesson.feedback?.comments) ||
      truthyText(lesson.feedback?.creativity) ||
      truthyText(lesson.feedback?.clarity) ||
      truthyText(lesson.feedback?.task_completion) ||
      truthyText(lesson.feedback?.rubric_json) ||
      modernFeedbackPresent ||
      truthyText(resultJson?.student_feedback?.feedback_text) ||
      truthyText(resultJson?.feedback_text) ||
      signalLines.length > 0 ||
      Boolean(passed) ||
      attempt != null ||
      Boolean(bestAttempt?.attempt) ||
      attempts.length > 0 ||
      Boolean(legacyCoachFeedback) ||
      Boolean(signalSummaryLine);
    const toggleWritingSignals = () => {
      setExpandedSignalsLessonKey((current) => (current === lessonId ? "" : lessonId));
    };

    if (showProgressTestTools && lesson?.status === "Completed") {
      const feedbackKeys = lesson.feedback && typeof lesson.feedback === "object" ? Object.keys(lesson.feedback) : [];
      const feedbackCommentKeys =
        lesson.feedback && typeof lesson.feedback?.comments === "object"
          ? Object.keys(lesson.feedback.comments)
          : [];
      // Dev-only trace for stale progress payloads.
      // eslint-disable-next-line no-console
      console.debug("DashboardV2 lesson trace", {
        lesson_id: lesson?.id || `W${lesson?.weekNum}L${lesson?.lesson}`,
        week: lesson?.weekNum,
        lesson: lesson?.lesson,
        status: lesson?.status,
        score,
        feedback_keys: feedbackKeys,
        feedback_comment_keys: feedbackCommentKeys,
        feedback_comments_present: truthyText(lesson?.feedback?.comments),
        result_json_student_feedback_present: Boolean(resultJson?.student_feedback),
        hasStoredReview,
      });
    }

    return (
      <>
        {lesson.status === "Completed" ? renderProgressLessonVideo(lessonVideoUrl) : null}
        <div className="dv-task"><b>Task:</b> {lesson.task}</div>

        {structuredSectionView ? (
          <div className="dv-submission">
            <b>Your Submission:</b>
            <div className="dv-submission-sections">
              {structuredSectionView.map((section) => (
                <div key={section.key} className="dv-submission-section">
                  <div className="dv-submission-section-label">{section.label}</div>
                  {section.description ? <div className="dv-submission-section-description">{section.description}</div> : null}
                  <div className="dv-submission-section-response">{section.value || section.content || "(No response)"}</div>
                </div>
              ))}
            </div>
          </div>
        ) : lesson.draft_text ? (
          <div className="dv-submission">
            <b>Your Submission:</b>
            <div>{lesson.draft_text}</div>
          </div>
        ) : null}

        {lesson.status === "Completed" && !hasStoredReview ? (
          <div className="dv-feedback">
            <b>Feedback:</b>
            <div>Stored submission and feedback are unavailable for this lesson.</div>
          </div>
        ) : null}

        {modernFeedbackPresent ? (
          <div className="dv-feedback progress-feedback-card">
            <div className="progress-feedback-topline">
              <div className="progress-feedback-score-block">
                <div className="progress-feedback-label">Score</div>
                <div className="progress-feedback-score">{score != null ? `${score}/100` : "—"}</div>
              </div>
              <div className="progress-feedback-meta">
                <span className="progress-feedback-badge">Completed</span>
                {attempt != null ? <span>Attempt: A{attempt}</span> : null}
                {bestAttempt?.attempt != null ? <span>Best attempt: A{bestAttempt.attempt}</span> : null}
                {score != null && passed != null ? <span>{passed ? "Passed" : "Needs work"}</span> : null}
              </div>
            </div>

            <div className="progress-feedback-body">
              {feedbackSections.map((section) => (
                <div key={section.label} className="progress-feedback-section">
                  <h4>{section.label}</h4>
                  <p>{section.text}</p>
                </div>
              ))}
            </div>

            {coachFeedbackText ? (
              <div className="progress-coach-card">
                <h4>Coach Feedback</h4>
                <p>{coachFeedbackText}</p>
              </div>
            ) : null}

            {hasWritingSignals ? (
              <div className="progress-writing-signals">
                <button
                  type="button"
                  className="progress-writing-signals-toggle"
                  onClick={toggleWritingSignals}
                  aria-expanded={signalAccordionOpen ? "true" : "false"}
                >
                  {signalAccordionOpen ? "Hide Writing Signals" : "Review Writing Signals"}
                </button>
                {signalAccordionOpen ? (
                  <div className="progress-writing-signals-body" aria-label="Writing Signals">
                    <h4>Writing Signals</h4>
                    <ul className="progress-writing-signals-list">
                      {writingSignalEntries.map((entry, index) => (
                        <li key={`${lessonId}-signal-${index}`}>
                          {formatSignalEvidence(entry)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          (score != null || passed != null || attempt != null || bestAttempt?.attempt || attempts.length > 1 || legacyCoachFeedback || signalSummaryLine || signalLines.length > 0) && (
            <div className="dv-feedback">
              <b>Feedback:</b>
              {score != null ? (
                <div>
                  Score: {score}/100{passed == null ? "" : passed ? " · Passed" : " · Needs work"}
                </div>
              ) : null}
              {attempt != null ? <div>Attempt: A{attempt}</div> : null}
              {bestAttempt?.attempt != null ? <div>Best attempt: A{bestAttempt.attempt}</div> : null}
              {legacyCoachFeedback ? <div>{legacyCoachFeedback}</div> : null}
              {signalSummaryLine ? <div>Writing signals: {signalSummaryLine}</div> : null}
              {signalLines.length > 0 ? <div>{signalLines.join(" • ")}</div> : null}

              {lesson.feedback?.rubric_json && <RubricBars rubricJson={lesson.feedback.rubric_json} />}
            </div>
          )
        )}
      </>
    );
  };

  const outerClass = inPortal ? "dv-embed" : "dv-wrap";

  const panelCloseButton = closeSidebarPanel ? (
    <button
      type="button"
      className="sl-close-btn sp-panel-close-btn"
      onClick={closeSidebarPanel}
      aria-label="Close progress panel"
      title="Close progress panel"
    >
      ×
    </button>
  ) : null;

  if (loading) return <div className={outerClass}><div className="dv-card dv-header">{panelCloseButton}<div className="dv-loading">Loading dashboard…</div></div></div>;
  if (!data) return <div className={outerClass}><div className="dv-card dv-header">{panelCloseButton}<div className="dv-loading">No data available</div></div></div>;
  if (data.error) return (
    <div className={outerClass}>
      <div className="dv-card dv-header">
        {panelCloseButton}
        <h1 className="dv-title">Progress</h1>
        <p className="dv-sub" style={{color: "#b00020"}}>Error: {data.error}</p>
        <p className="dv-sub">If this is a new student ID, make sure the user exists in the system.</p>
      </div>
    </div>
  );

  return (
    <div className={outerClass}>
      {/* Top Nav (standalone only) */}
      {!inPortal && !disableStandaloneNav && (
        <header className="dv-nav">
          <div className="dv-nav-left">
            <img src={process.env.PUBLIC_URL + "/hirilogo.png"} alt="HIRI logo" className="dv-logo" />
            <span className="dv-brand">HOW I REMEMBER IT</span>
          </div>
          <div className="dv-nav-right">
            <button className="dv-link" onClick={() => navigate(`/student/dashboard/${studentId}`)}>Back to Portal</button>
          </div>
        </header>
      )}

      {/* Header card */}
      <section className="dv-card dv-header reveal" style={{"--delay":".05s"}}>
        {panelCloseButton}
        <h1 className="dv-title">Progress</h1>
        <p className="dv-sub">Student <b>{data.student?.name || "Unknown"}</b> (ID: {data.student?.student_id || studentId})</p>
        {showProgressTestTools ? (
          <div className="dv-dev-progress-tools">
            <button
              className="dv-link"
              type="button"
              onClick={startProgressTest}
              disabled={progressTestRunning}
            >
              Auto-Test Progress Panel
            </button>
            <button
              className="dv-link"
              type="button"
              onClick={stopProgressTest}
              disabled={!progressTestRunning}
            >
              Stop
            </button>
            <button
              className="dv-link"
              type="button"
              onClick={resetProgressTest}
              disabled={!progressTestActive}
            >
              Reset
            </button>
            {progressTestActive && progressTestScenarioName ? (
              <span className="dv-dev-progress-label">
                DEV Progress Test: {progressTestScenarioName}
              </span>
            ) : null}
            <button className="dv-link" type="button" onClick={refreshDashboardV2} disabled={progressTestRunning}>
              Refresh Progress
            </button>
          </div>
        ) : null}
      </section>

      <SummaryPanel summary={data.summary} />

      {showCertificateGenerator ? (
        <section className="dv-card dv-summary reveal" style={{ "--delay": ".075s" }}>
          <div className="dv-summary-k" style={{ marginBottom: 10 }}>Certificate Generator</div>
          <div className="dv-summary-v" style={{ marginBottom: 12 }}>
            Generate and preview HIRI certificates using the current template and student data.
          </div>
          <div className="dv-cert-generator-actions" style={{ display: "grid", gap: 12 }}>
            <button
              type="button"
              className="dv-link"
              onClick={generateTestCertificate}
              disabled={certificateLoading}
            >
              {certificateLoading ? "Generating..." : "Generate Test Certificate"}
            </button>
            {certificateError ? <div className="dv-cert-error" style={{ color: "#fca5a5" }}>{certificateError}</div> : null}
            {certificateState ? (
              <div className="dv-cert-preview" style={{ display: "grid", gap: 8 }}>
                <div><b>Certificate ID:</b> {certificateState.certificate_id}</div>
                <a href={`${API}/api/certificates/verify/${encodeURIComponent(certificateState.certificate_id)}`} target="_blank" rel="noreferrer">
                  View JSON
                </a>
                <a href={`${API}${certificateState.download_url}`} target="_blank" rel="noreferrer">
                  Download Certificate
                </a>
                <a href={certificateState.verification_url} target="_blank" rel="noreferrer">
                  Open Verification Page
                </a>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <TrendGraph trend={data.trend} />

      <section className="dv-stack">
        {weekCards.map((week, wi) => {
          const weekHeading = week.isFutureWeek
            ? `Week ${week.weekNum} (Locked)`
            : week.isUnlockedFutureWeek
            ? `Week ${week.weekNum} (Open)`
            : week.isCurrentWeek
            ? `Week ${week.weekNum} (Current)`
            : `Week ${week.weekNum} (Completed)`;

          if (week.isPastWeek) {
            return (
              <details
                key={week.weekNum}
                className="dv-card dv-week dv-week-past reveal"
                style={{ "--delay": `${0.1 + wi * 0.05}s` }}
                open={selectedLessonWeekNum === week.weekNum}
              >
                <summary className="dv-week-summary">{weekHeading}</summary>
                {week.lessons.map((lesson) => {
                  const key = lessonKey(week.weekNum, lesson.lesson);
                  const isOpen = selectedLessonKey === key;
                  const isCompleted = lesson.status === "Completed";

                  return (
                    <div
                      key={lesson.lesson}
                      className={`dv-lesson-item ${isOpen ? "is-open" : ""}`.trim()}
                      data-lesson-key={key}
                      ref={(node) => {
                        if (!node) {
                          autoScrollLessonRef.current.delete(key);
                          return;
                        }
                        autoScrollLessonRef.current.set(key, node);
                      }}
                    >
                      <button
                        type="button"
                        className={`dv-lesson dv-lesson-button ${isCompleted ? "dv-lesson-clickable" : ""} ${
                          isOpen ? "is-selected" : ""
                        }`.trim()}
                        disabled={!isCompleted}
                        onClick={() => {
                          if (isCompleted) {
                            setSelectedLessonKey((prev) => (prev === key ? "" : key));
                          }
                        }}
                        aria-pressed={isOpen}
                      >
                        <div className="dv-lesson-head">
                          <h3 className="dv-h3 progress-lesson-title">Lesson {lesson.lesson}: {lesson.topic}</h3>
                          <div>
                            <span className="dv-status is-completed">Completed</span>
                            {formatLessonCompletedAt(lesson.completed_at) ? (
                              <div className="dv-completed-at">{formatLessonCompletedAt(lesson.completed_at)}</div>
                            ) : null}
                          </div>
                        </div>
                      </button>
                      {isOpen ? <div className="dv-lesson-accordion">{renderLessonRichContent(lesson)}</div> : null}
                    </div>
                  );
                })}
              </details>
            );
          }

          return (
            <div key={week.weekNum} className={`dv-card dv-week reveal ${week.isFutureWeek ? "dv-week-locked" : ""}`.trim()} style={{"--delay": `${0.1 + wi*0.05}s`}}>
              <h2 className="dv-h2">{weekHeading}</h2>

              {week.isFutureWeek ? (
                <div className="dv-week-lock-note">Complete Week {Math.max(1, week.weekNum - 1)} to unlock</div>
              ) : null}

              {week.lessons.map((lesson, li) => {
                const key = lessonKey(week.weekNum, lesson.lesson);
                const isOpen = selectedLessonKey === key;
                const isCompleted = lesson.status === "Completed";

                return (
                  <div
                    key={lesson.lesson}
                    className={`dv-lesson-item ${isOpen ? "is-open" : ""}`.trim()}
                    data-lesson-key={key}
                    ref={(node) => {
                      if (!node) {
                        autoScrollLessonRef.current.delete(key);
                        return;
                      }
                      autoScrollLessonRef.current.set(key, node);
                    }}
                  >
                    <button
                      type="button"
                      className={`dv-lesson dv-lesson-button ${
                        lesson.isLocked ? "dv-lesson-locked" : ""
                      } ${isCompleted ? "dv-lesson-clickable" : ""} ${isOpen ? "is-selected" : ""}`.trim()}
                      style={{ "--lesson-delay": `${li * 36}ms` }}
                      disabled={!isCompleted}
                      onClick={() => {
                        if (isCompleted) {
                          setSelectedLessonKey((prev) => (prev === key ? "" : key));
                        }
                      }}
                      aria-pressed={isOpen}
                    >
                      <div className="dv-lesson-head">
                        <h3 className="dv-h3 progress-lesson-title">Lesson {lesson.lesson}: {lesson.topic}</h3>
                        <div
                          className={`dv-status ${
                            lesson.status === "Completed"
                              ? "is-completed"
                              : lesson.status === "In Progress"
                              ? "is-in-progress"
                              : lesson.status === "Ready"
                              ? "is-ready"
                              : "is-locked"
                          }`.trim()}
                        >
                          <span>{lesson.status}</span>
                          {lesson.status === "Completed" && formatLessonCompletedAt(lesson.completed_at) ? (
                            <div className="dv-completed-at">{formatLessonCompletedAt(lesson.completed_at)}</div>
                          ) : null}
                        </div>
                      </div>
                    </button>
                    {isOpen ? <div className="dv-lesson-accordion">{renderLessonRichContent(lesson)}</div> : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </section>
      {!inPortal && <footer className="dv-footer">© 2026 How I Remember It</footer>}
    </div>
  );
}
